import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { debounce } from "lodash";
import { Check, ChevronDown, Copy, Edit, PlusCircle, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../../../components/ui/popover";
import { ButtonGroup } from "../../../../../../components/ui/button-group";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../../../../components/ui/command";
import Button from "../../../../../../components/shared/Button";
import Spinner from "../../../../../../components/shared/Spinner";
import AddTemplate from "./AddTemplate";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../utils/api";
import { useVisible } from "../../../../../../utils/hooks/useVisible";
import { useAccess } from "../../../../../../utils/hooks/access";
import { cn } from "../../../../../../lib/utils";

function ScheduleTemplates() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState<string | undefined>();
    const { type, clearSelectedEvents, dispatch } = useScheduleContext();
    const isTeamTemplate = ["template", "profile"].includes(type);
    const canCreate = useAccess(isTeamTemplate ? "schedule.canCreate" : "schedule.canCreateMySchedule");
    const canEdit = useAccess(isTeamTemplate ? "schedule.canEdit" : "schedule.canEditMySchedule");
    const canDelete = useAccess(isTeamTemplate ? "schedule.canDelete" : "schedule.canDeleteMySchedule");
    const { authGet } = useApi();
    const [filters] = useFilters();

    const [selectedItem, setSelectedItem] = useState<any>(filters?.template);
    const { open, visible, close, selected } = useVisible();
    const { ref, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
    });
    const queryClient = useQueryClient();

    const url = useMemo(() => {
        switch (type) {
            case "my-profile": {
                return "/my/schedule-templates";
            }
            case "profile":
            case "template":
            default: {
                return "/schedule-templates";
            }
        }
    }, [type]);

    const debouncedSetSearch = useMemo(() => {
        return debounce((value: string) => {
            setSearch(value);
            queryClient.removeQueries([url] as any);
        }, 1000)
    }, [queryClient, url]);

    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [debouncedSetSearch]);

    const {
        data,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
    } = useInfiniteQuery({
        queryKey: [url, { search, userId: id }],
        queryFn: async ({ queryKey, pageParam }: any) => {
            const [endpoint, params] = queryKey;
            let filters: any = params?.search ? { search: params?.search } : {};
            // if (type === "profile") {
            //     filters.userId = params.userId;
            // }
            const response = await authGet(endpoint, {
                params: {
                    ...filters,
                    page: pageParam,
                    perPage: 20
                }
            });
            return response;
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            const loadedItems = allPages.reduce((acc, page) => acc + (page?.result?.length || 0), 0);
            if (loadedItems < (lastPage?.totalItems || 0)) {
                return allPages.length + 1;
            }
            return undefined;
        },
        enabled: ["template", "profile", "my-profile"].includes(type),
        // refetchOnReconnect: true,
        // staleTime: 0
    });

    useEffect(() => {
        if (!inView) return;
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView]);

    const _data = useMemo(() => {
        const itemsMap = new Map();

        if (filters?.template?.id) {
            itemsMap.set(filters.template.id, filters.template);
        }

        data?.pages?.forEach((page: any) => {
            page?.result?.forEach((item: any) => {
                if (item?.id) {
                    itemsMap.set(item.id, item);
                }
            });
        });

        let allUniqueItems = Array.from(itemsMap.values());

        if (search && search.trim() !== "") {
            const lowerSearch = search.toLowerCase();
            allUniqueItems = allUniqueItems.filter((item) =>
                item.name?.toLowerCase().includes(lowerSearch)
            );
        }

        const selectedId = selectedItem?.id;
        if (!selectedId) return allUniqueItems;

        const selected = allUniqueItems.find((item) => item.id === selectedId);
        const rest = allUniqueItems.filter((item) => item.id !== selectedId);

        return [
            ...(selected ? [selected] : []),
            ...rest,
        ];
    }, [data?.pages, filters?.template, selectedItem, search]);

    useEffect(() => {
        if (!isLoading) {
            dispatch({ type: "set-templates-data", payload: _data });
        }
    }, [isLoading, dispatch, _data]);

    useEffect(() => {
        if (!selectedItem && _data?.length > 0) {
            setSelectedItem({ id: _data?.[0]?.id, name: _data?.[0]?.name });
        }
    }, [_data, selectedItem]);

    return ["template", "profile", "my-profile"].includes(type) ? (
        <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-py-3">
            <ButtonGroup>
                <Popover>
                    <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                            className="tw-font-normal tw-text-foreground tw-h-9 tw-max-w-52 tw-gap-2"
                            variant="outline"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span className={cn(!selectedItem?.name && "tw-text-muted-foreground", "tw-truncate tw-text-start tw-text-sm tw-max-w-36")}>
                                {selectedItem?.name || t("schedule-templates")}
                            </span>
                            <ChevronDown size={14} className="tw-shrink-0 tw-text-muted-foreground" />
                        </Button>
                    </PopoverTrigger>

                <PopoverContent align="start" className="tw-p-1 tw-w-72">
                    <Command shouldFilter={false}>
                        <CommandInput
                            value={search}
                            placeholder={t("search") + "..."}
                            onValueChange={(val) => {
                                setSearch(val);
                                debouncedSetSearch(val);
                            }}
                        />
                        <CommandList>
                            <CommandEmpty>
                                {isLoading ? <Spinner /> : t("no-result")}
                            </CommandEmpty>
                            <CommandGroup>
                                {_data?.map((item: any) => {
                                    const checked = selectedItem?.id === item.id;
                                    const disabled = selectedItem?.id === item.id || (!hasNextPage && _data?.length === 1);
                                    return (
                                        <CommandItem
                                            key={item.id}
                                            value={item.id}
                                            className={cn(
                                                checked && "tw-bg-primary/10",
                                                "tw-cursor-pointer tw-flex tw-justify-between tw-items-center tw-gap-2"
                                            )}
                                            onSelect={() => {
                                                setSelectedItem({ id: item.id, name: item.name });
                                                clearSelectedEvents();
                                                setSearchParams((prev) => {
                                                    const next = new URLSearchParams(prev);
                                                    next.set("template", JSON.stringify({ id: item.id, name: item.name }));
                                                    return next;
                                                });
                                            }}
                                        >
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1">
                                                {checked
                                                    ? <Check size={14} className="tw-shrink-0 tw-text-primary" />
                                                    : <div className="tw-w-3.5 tw-shrink-0" />
                                                }
                                                <span className="tw-truncate tw-text-sm">
                                                    {item.name}
                                                </span>
                                            </div>

                                            <div
                                                className="tw-flex tw-items-center tw-gap-1 tw-shrink-0"
                                                onClick={(e) => e.stopPropagation()}
                                                onPointerDown={(e) => e.stopPropagation()}
                                            >
                                                {(canEdit && type !== "my-profile") && (
                                                    <button
                                                        className={cn(
                                                            "tw-rounded tw-p-1 tw-transition-colors",
                                                            disabled ? "tw-cursor-default" : "tw-cursor-pointer hover:tw-bg-neutral-100"
                                                        )}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            open({ ...item, mode: "edit" });
                                                        }}
                                                        disabled={disabled}
                                                    >
                                                        <Edit size={14} className={cn(disabled ? "tw-text-neutral-400" : "tw-text-primary")} />
                                                    </button>
                                                )}

                                                {(canCreate && type !== "my-profile") && (
                                                    <button
                                                        className="tw-rounded tw-p-1 tw-transition-colors tw-cursor-pointer hover:tw-bg-neutral-100"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            open({ ...item, mode: "clone" });
                                                        }}
                                                    >
                                                        <Copy size={14} className="tw-text-indigo-500" />
                                                    </button>
                                                )}

                                                {(canDelete && type !== "my-profile") && (
                                                    <button
                                                        className={cn(
                                                            "tw-rounded tw-p-1 tw-transition-colors",
                                                            disabled ? "tw-cursor-default" : "tw-cursor-pointer hover:tw-bg-neutral-100"
                                                        )}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            open({ ...item, mode: "delete" });
                                                        }}
                                                        disabled={disabled}
                                                    >
                                                        <Trash2 size={14} className={cn(disabled ? "tw-text-neutral-400" : "tw-text-rose-500")} />
                                                    </button>
                                                )}
                                            </div>
                                        </CommandItem>
                                    )
                                })}
                            </CommandGroup>
                            {(hasNextPage && !isLoading) && (
                                <div
                                    ref={ref}
                                    className="tw-py-2 tw-text-center tw-text-sm tw-text-muted-foreground"
                                >
                                    {isFetchingNextPage ? t("loading") : t("loading-more")}
                                </div>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
                </Popover>

                {(canCreate && ["template", "profile"].includes(type)) && (
                    <Button
                        variant="outline"
                        size="icon"
                        className="tw-h-9 tw-w-9"
                        tooltip={t("create-template")}
                        onClick={() => open()}
                    >
                        <PlusCircle size={14} className="tw-text-primary" />
                    </Button>
                )}
            </ButtonGroup>

            <AddTemplate
                values={selected}
                close={close}
                visible={visible}
                refetch={() => queryClient.invalidateQueries({ queryKey: [url, { search, userId: id }] })}
            />
        </div>
    ) : null;
}

export default ScheduleTemplates;
